import React, { useMemo, useState } from "react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Plus, Upload, Download } from "lucide-react";
import { StaffNavigation } from "./components/Navigtaion";
import { StaffFilters } from "./components/StaffFilters";
import { StaffTable } from "./components/StaffTable";
import { ImportStaffModal } from "./components/ImportStaffModal";
import { useTranslation } from "react-i18next";
import { UsersKpiPanel } from "@/components/pages/users/components/UsersKpiPanel";
import { useUsersKpis } from "@/components/pages/users/hooks/useUsers";
import {
  useStaff,
  useDeleteStaff,
  useToggleFrontDeskActive,
  useUpdateStaff,
} from "./hooks/useStaff";
import { useStaffAvailabilityRange } from "@/hooks/useStaffSchedule";
import { GetStaffFilters, StaffWithRelations } from "@/services/staffService";
import { useNavigate } from "react-router-dom";
import { useCurrentHotelId } from "@/hooks/useCurrentHotelId";
import { useCSVExport } from "@/hooks/useCSVExport";
import {
  getStaffPresentationLabels,
  mapStaffToExportRow,
} from "./utils/staffExport";
import { Role } from "@/types/user";

type StaffStatusFilter = "all" | "on-site" | "not-on-site";

export const StaffPage: React.FC = () => {
  const navigate = useNavigate();
  const hotelId = useCurrentHotelId();
  const { exportToCSV } = useCSVExport();
  const { t } = useTranslation();

  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  const [filters, setFilters] = useState<GetStaffFilters>({
    currentPage: 1,
    perPage: 10,
  });
  const [statusFilter, setStatusFilter] = useState<StaffStatusFilter>("all");

  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc" | null>(null);

  const staffFilter: GetStaffFilters = useMemo(() => {
    return {
      ...filters,
      currentPage: 1,
      perPage: 1000,
      sortField: sortBy ?? undefined,
      sortOrder: sortOrder ?? undefined,
      hotelId: hotelId ?? undefined,
    };
  }, [filters, hotelId, sortBy, sortOrder]);

  const { data: staffData, isLoading, refetch } = useStaff(staffFilter);
  const today = format(new Date(), "yyyy-MM-dd");
  const usersKpiFilters = useMemo(
    () => ({
      hotelId: hotelId ?? undefined,
      date: today,
    }),
    [hotelId, today],
  );
  const { data: usersKpis, isLoading: isUsersKpisLoading } = useUsersKpis(
    usersKpiFilters,
    Boolean(hotelId),
  );
  const { data: todayAvailability, isLoading: isTodayAvailabilityLoading } =
    useStaffAvailabilityRange(hotelId ?? "", today, today);
  const deleteStaff = useDeleteStaff();
  const updateStaff = useUpdateStaff();
  const toggleFrontDeskActive = useToggleFrontDeskActive();

  const staffStatusMap = useMemo(() => {
    const map = new Map<string, Exclude<StaffStatusFilter, "all">>();

    if (!todayAvailability) {
      return map;
    }

    for (const availability of todayAvailability) {
      const day = availability.days[0];
      if (!day) {
        continue;
      }

      const nextStatus: Exclude<StaffStatusFilter, "all"> = day.isAvailable
        ? "on-site"
        : "not-on-site";

      map.set(availability.staffId, nextStatus);
      if (availability.profileId) {
        map.set(availability.profileId, nextStatus);
      }
    }

    return map;
  }, [todayAvailability]);

  const filteredStaff = useMemo(() => {
    const staffUsers = staffData?.users ?? [];

    if (statusFilter === "all") {
      return staffUsers;
    }

    return staffUsers.filter((user) => {
      if (!user.staff) {
        return false;
      }
      const staffId = user.staff?.id;
      const currentStatus =
        (staffId ? staffStatusMap.get(staffId) : undefined) ??
        staffStatusMap.get(user.id);

      if (statusFilter === "on-site") {
        return currentStatus === "on-site";
      }

      return currentStatus !== "on-site";
    });
  }, [staffData, staffStatusMap, statusFilter]);

  const teamOnShiftCounts = useMemo(() => {
    const staffUsers = staffData?.users ?? [];

    return staffUsers.reduce<Record<string, number>>((counts, user) => {
      if (!user.staff) {
        return counts;
      }

      const currentStatus =
        staffStatusMap.get(user.staff.id) ?? staffStatusMap.get(user.id);

      if (currentStatus !== "on-site") {
        return counts;
      }

      for (const userTeam of user.staff.userTeams) {
        counts[userTeam.teamId] = (counts[userTeam.teamId] ?? 0) + 1;
      }

      return counts;
    }, {});
  }, [staffData, staffStatusMap]);

  const paginatedStaff = useMemo(() => {
    const currentPage = filters.currentPage || 1;
    const perPage = filters.perPage || 10;
    const startIndex = (currentPage - 1) * perPage;
    const endIndex = startIndex + perPage;

    return filteredStaff.slice(startIndex, endIndex);
  }, [filteredStaff, filters.currentPage, filters.perPage]);

  const isTableLoading = isLoading || isTodayAvailabilityLoading;

  const handlePageChange = (page: number) => {
    setFilters({ ...filters, currentPage: page });
  };

  const handleStatusFilterChange = (value: StaffStatusFilter) => {
    setStatusFilter(value);
    setFilters((currentFilters) => ({
      ...currentFilters,
      currentPage: 1,
    }));
  };

  const handleSort = (field: string) => {
    if (sortBy !== field) {
      setSortBy(field);
      setSortOrder("asc");
    } else {
      if (sortOrder === "asc") setSortOrder("desc");
      else if (sortOrder === "desc") {
        setSortBy(null);
        setSortOrder(null);
      } else setSortOrder("asc");
    }
  };

  const handleDelete = (id: string) => {
    if (confirm("Are you sure you want to delete this staff member?")) {
      deleteStaff.mutate(id);
    }
  };

  const handleStatusToggle = (staff: StaffWithRelations) => {
    if (staff.staff) {
      updateStaff.mutate({
        id: staff.id,
        data: { isActive: !staff.staff.isActive },
      });
      return;
    }

    if (staff.role === Role.FRONT_DESK) {
      toggleFrontDeskActive.mutate({
        id: staff.id,
        isActive: staff.status === "INACTIVE",
      });
    }
  };

  const handleCSVExport = () => {
    if (filteredStaff.length === 0) return;
    const labels = getStaffPresentationLabels(t);
    const rows = filteredStaff.map((staff) =>
      mapStaffToExportRow(staff, labels),
    );
    exportToCSV(rows, "staff-export.csv");
  };

  const totalPages = Math.ceil(filteredStaff.length / (filters.perPage || 10));

  return (
    <div className="flex h-full flex-col gap-6 p-6">
      <div className="flex flex-col gap-6">
        <StaffNavigation activeTab="staff" />

        <div className="flex items-center justify-between">
          <StaffFilters
            filters={filters}
            onFilterChange={setFilters}
            statusFilter={statusFilter}
            onStatusFilterChange={handleStatusFilterChange}
            teamOnShiftCounts={teamOnShiftCounts}
          />

          <div className="flex gap-2">
            <Button
              type="button"
              variant="new_outline"
              onClick={() => setIsImportModalOpen(true)}
            >
              <Upload />
              {t("staff.actions.import", "Import")}
            </Button>
            <Button
              type="button"
              variant="new_outline"
              onClick={handleCSVExport}
            >
              <Download />
              {t("staff.actions.export", "Export")}
            </Button>
            <Button
              type="button"
              variant="new_default"
              onClick={() => navigate("/staff/add")}
            >
              <Plus />
              {t("staff.actions.addUser", "Add user")}
            </Button>
          </div>
        </div>

        {hotelId ? (
          <UsersKpiPanel summary={usersKpis} isLoading={isUsersKpisLoading} />
        ) : null}

        <StaffTable
          data={paginatedStaff}
          isLoading={isTableLoading}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
          currentPage={filters.currentPage || 1}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          onDelete={handleDelete}
          onStatusToggle={handleStatusToggle}
          staffStatusMap={staffStatusMap}
        />
      </div>

      <ImportStaffModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onSuccess={() => refetch()}
      />
    </div>
  );
};
